import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import WorkflowTemplateModal from './WorkflowTemplateModal';

const WorkflowTemplates = ({ data, onDataChange, user }) => {
  const [templates, setTemplates] = useState(data.templates || []);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [availableApprovers, setAvailableApprovers] = useState([]);

  useEffect(() => {
    setTemplates(data.templates || []);
  }, [data.templates]);

  useEffect(() => {
    loadCategories();
    loadAvailableApprovers();
  }, []);

  const loadCategories = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/workflow/categories', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.success) {
        setCategories(result.data);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadAvailableApprovers = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/workflow/approvers', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.success) {
        setAvailableApprovers(result.data);
      }
    } catch (error) {
      console.error('Error loading available approvers:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleCreateTemplate = () => {
    setSelectedTemplate(null);
    setShowCreateModal(true);
  };

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setShowCreateModal(true);
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this workflow template?')) {
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`/api/workflow/templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        onDataChange(); // Refresh data
        alert('Template deleted successfully');
      } else {
        const error = await response.json();
        alert(`Error: ${error.message}`);
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Error deleting template');
    } finally {
      setLoading(false);
    }
  };

  const saveTemplate = async (templateData) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      const url = selectedTemplate 
        ? `/api/workflow/templates/${selectedTemplate.id}`
        : '/api/workflow/templates';
      
      const method = selectedTemplate ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(templateData)
      });

      const result = await response.json();
      
      if (result.success) {
        onDataChange(); // Refresh data
        alert(`Template ${selectedTemplate ? 'updated' : 'created'} successfully`);
        return true;
      } else {
        alert(`Error: ${result.message}`);
        return false;
      }
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Error saving template');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const getStepTypeColor = (approverType) => {
    if (!approverType) return 'bg-gray-100 text-gray-800';
    
    const colors = {
      'direct_manager': 'bg-blue-100 text-blue-800',
      'hr_manager': 'bg-green-100 text-green-800',
      'senior_manager': 'bg-purple-100 text-purple-800',
      'department_manager': 'bg-indigo-100 text-indigo-800',
      'director': 'bg-red-100 text-red-800',
      'specific_user': 'bg-yellow-100 text-yellow-800',
      'role_based': 'bg-gray-100 text-gray-800',
      'multiple': 'bg-orange-100 text-orange-800'
    };
    return colors[approverType] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Workflow Templates</h2>
          <p className="text-sm text-gray-500">
            Manage approval workflow templates for different processes
          </p>
        </div>
        
        <button
          onClick={handleCreateTemplate}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
        >
          <PlusIcon className="h-4 w-4" />
          <span>Create Template</span>
        </button>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((template) => (
          <div key={template.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
            {/* Template Header */}
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <h3 className="text-lg font-medium text-gray-900">{template.name}</h3>
                  {template.is_default && (
                    <span className="inline-flex px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                      Default
                    </span>
                  )}
                </div>
                
                {template.category_name && (
                  <div className="flex items-center space-x-1 mb-1">
                    <TagIcon className="h-3 w-3 text-gray-400" />
                    <span className="text-xs text-gray-500">{template.category_name}</span>
                  </div>
                )}
                
                <p className="text-sm text-gray-500">{template.description}</p>
              </div>
              
              <div className="flex items-center space-x-1 ml-4">
                {template.is_active ? (
                  <CheckCircleIcon className="h-5 w-5 text-green-500" title="Active" />
                ) : (
                  <XCircleIcon className="h-5 w-5 text-red-500" title="Inactive" />
                )}
              </div>
            </div>

            {/* Workflow Steps */}
            <div className="space-y-2 mb-4">
              <h4 className="text-sm font-medium text-gray-700">Workflow Steps:</h4>
              {template.steps?.filter(step => step.id && step.step_name).map((step, index) => (
                <div key={step.id || index} className="flex items-center justify-between py-1">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500">{step.step_order || index + 1}.</span>
                    <span className="text-sm text-gray-700">{step.step_name || 'Unnamed Step'}</span>
                  </div>
                  <span className={`inline-flex px-2 py-1 text-xs rounded-full ${getStepTypeColor(step.approver_type)}`}>
                    {step.approver_type ? step.approver_type.replace('_', ' ') : 'N/A'}
                  </span>
                </div>
              )) || (
                <p className="text-sm text-gray-500 italic">No steps defined</p>
              )}
              {template.steps?.filter(step => step.id && step.step_name).length === 0 && (
                <p className="text-sm text-gray-500 italic">No steps defined</p>
              )}
            </div>

            {/* Template Footer */}
            <div className="border-t border-gray-200 pt-4">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">
                  Created: {formatDate(template.created_at)}
                </span>
                
                <div className="flex space-x-2">
                  <button
                    onClick={() => setSelectedTemplate(template)}
                    className="p-1 text-gray-400 hover:text-blue-600"
                    title="View Details"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleEditTemplate(template)}
                    className="p-1 text-gray-400 hover:text-blue-600"
                    title="Edit Template"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteTemplate(template.id)}
                    className="p-1 text-gray-400 hover:text-red-600"
                    title="Delete Template"
                    disabled={template.is_default}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}

        {templates.length === 0 && (
          <div className="col-span-full text-center py-12">
            <div className="text-gray-400 mb-4">
              <PlusIcon className="mx-auto h-12 w-12" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No workflow templates</h3>
            <p className="text-gray-500 mb-4">Create your first workflow template to get started.</p>
            <button
              onClick={handleCreateTemplate}
              className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
            >
              <PlusIcon className="h-4 w-4" />
              <span>Create Template</span>
            </button>
          </div>
        )}
      </div>

      {/* Workflow Template Modal */}
      <WorkflowTemplateModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setSelectedTemplate(null);
        }}
        template={selectedTemplate}
        categories={categories}
        availableApprovers={availableApprovers}
        onSave={saveTemplate}
      />

      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-40">
          <div className="bg-white rounded-lg p-6 flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="text-gray-700">Processing...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowTemplates;